import { Logo } from '@components'
import { SearchBoxIcon, Tooltip } from '@components-deprecated'

import { ButtonsGroup, Flex, notify, Spacing } from '@billy/design-system'
import { css } from '@emotion/core'
import React, { useCallback, useState } from 'react'
import { DragDropContext, DragStart, DragUpdate, DropResult } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from 'reselect'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { SpecificState } from '../../../types/reduxSaga-deprecated'
import { reactClass } from '../../../utils'
import { store } from '../../index'
import {
  bankLinesAndTransactionsRequested,
  createPlaceholder,
  deleteBankLines,
  destroyPlaceholders,
  dndBankLines,
  dndBillyTransactions,
  filtersChanged,
} from '../action-creators'
import { MAX_BULK_BANK_LINE_UPDATE_RECORDS } from '../constants/bankReconciliationDifferenceTypeTKeys'
import { BankLineUpdate, updateBankLines } from '../query-api'
import { ReceiptState } from '../types'
import { cleanId } from '../utils'
import { BankLineGroupFiltersPanel } from './BankLineGroupFiltersPanel'
import { BankLogo } from './BankLogo'
import { BulkReconciliationButton } from './BulkReconciliationButton'
import { DownloadLinesButton } from './DownloadLinesButton'
import { FilterDropdownButton } from './FilterDropdownButton'
import { MatchList } from './MatchList'
import { ReconcilablePostingFiltersPanel } from './ReconcilablePostingFiltersPanel'
import { ReconcileAllButton } from './ReconcileAllButton'
import { SelectionPanelActions } from './SelectionPanelActions'
import * as Styled from './styles'
import { ToggleDeletionModeButton } from './ToggleDeletionModeButton'
import { ToggleMissingReceiptsModeButton } from './ToggleMissingReceiptsModeButton'
import { TransactionList } from './TransactionList'

export const BankRecon = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [matchDeletionMode, setMatchDeletionMode] = useState<boolean>(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isMissingReceiptsMode, setMissingReceiptsMode] = useState(false)
  const bankLineMatches = useSelector(
    createSelector(
      (state: SpecificState) => state.bankReconciliation.bankLineMatches,
      (bankLineMatches) => bankLineMatches,
    ),
  )
  const bankLineMatchCount = bankLineMatches?.length

  const handleOnDragStart = useCallback(
    ({ source, type }: DragStart) => {
      dispatch(createPlaceholder({ source, type }))
    },
    [dispatch],
  )

  const onChangeBankLineGroupSearchValue = useCallback(
    (bankLineGroupSearchValue: string) => {
      dispatch(filtersChanged({ bankLineGroupSearchValue }))
    },
    [dispatch],
  )

  const onChangeReconcilablePostingSearchValue = useCallback(
    (reconcilablePostingSearchValue: string) => {
      dispatch(filtersChanged({ reconcilablePostingSearchValue }))
    },
    [dispatch],
  )

  const handleOnDragUpdate = useCallback((update: DragUpdate) => null, [])

  const handleOnDragEnd = useCallback(
    ({ destination, source, draggableId, type }: DropResult) => {
      // Destroy any eventual temporary placeholders
      dispatch(destroyPlaceholders())

      // Dropped outside a droppable
      if (!destination) {
        return
      }

      // Dropped in the same group or source
      if (destination.droppableId === source.droppableId) {
        return
      }

      // Otherwise...
      if (type === 'bankline') {
        dispatch(
          dndBankLines({
            id: draggableId,
            matchId: destination.droppableId,
            sourceMatchId: source.droppableId,
          }),
        )
      } else if (type === 'billytransaction') {
        const state = store.getState() as SpecificState
        const billyTransactions = state.bankReconciliation.billyTransactions || []
        const bankLineSubjectAssociations = state.bankReconciliation.bankLineSubjectAssociations || []
        const [, rawSourceMatchId] = cleanId(source.droppableId)
        const billyTransaction = billyTransactions?.find((transaction) => transaction.id === draggableId)
        const bankLineSubjectAssociation = bankLineSubjectAssociations?.find((blsa) => {
          return blsa.matchId === rawSourceMatchId && blsa.subjectReference.split(':')[1] === draggableId
        })

        dispatch(
          dndBillyTransactions({
            id: draggableId,
            matchId: destination.droppableId,
            sourceMatchId: source.droppableId,
            billyTransaction,
            bankLineSubjectAssociation,
          }),
        )
      }
    },
    [dispatch],
  )

  const requestDeleteSelectedLines = useCallback(
    (idsToDelete: string[]) => {
      if (idsToDelete.length) {
        dispatch(deleteBankLines({ matchIds: idsToDelete }))
      }
    },
    [dispatch],
  )

  const enableMissingReceiptsMode = useCallback(() => {
    setMissingReceiptsMode(true)
  }, [])

  const getBankLinesForUpdate = useCallback(() => {
    return selectedIds.reduce((accumulator: BankLineUpdate[], selectedId) => {
      const { bankLines } = bankLineMatches.find(({ id }) => selectedId === id) || {}
      const result = [...accumulator]

      if (bankLines) {
        for (const bankLine of bankLines) {
          result.push({ id: bankLine.id, receiptState: ReceiptState.MissingReceipt })
        }
      }

      return result
    }, [])
  }, [bankLineMatches, selectedIds])

  const handleEditFinish = useCallback(async () => {
    if (isMissingReceiptsMode) {
      setMissingReceiptsMode(false)
      const bankLinesToUpdate = getBankLinesForUpdate()
      const chunks: BankLineUpdate[][] = []

      for (let i = 0; i < bankLinesToUpdate.length; i += MAX_BULK_BANK_LINE_UPDATE_RECORDS) {
        chunks.push(bankLinesToUpdate.slice(i, i + MAX_BULK_BANK_LINE_UPDATE_RECORDS))
      }

      const results = await Promise.allSettled(chunks.map((chunk) => updateBankLines(chunk)))

      const hasSuccess = results.some((result) => result.status === 'fulfilled')
      const hasFailure = results.some((result) => result.status === 'rejected')

      if (hasSuccess) {
        dispatch(bankLinesAndTransactionsRequested())
      }

      if (hasFailure) {
        notify({
          id: NotificationKeys.BankReconciliationUpdateBankLinesError,
          variant: 'error',
          message: t('bankreconciliation.update_bank_lines_error'),
        })
      }
    } else if (matchDeletionMode) {
      setMatchDeletionMode(false)
    }
  }, [dispatch, getBankLinesForUpdate, isMissingReceiptsMode, matchDeletionMode, t])

  return (
    <DragDropContext onDragStart={handleOnDragStart} onDragUpdate={handleOnDragUpdate} onDragEnd={handleOnDragEnd}>
      <Tooltip />
      <div
        className={reactClass('bankrecon-lists')}
        css={css`
          height: 100%;
          display: grid;
          grid-template-columns: 520fr 450px;
          grid-template-rows: 100px 1fr;
          column-gap: 80px;
        `}
      >
        <div>
          <BankLogo />
          <Flex
            className={reactClass('list-filters')}
            gap={Spacing.S12}
            css={css`
              position: relative;
              justify-content: space-between;
            `}
          >
            <Styled.NavSlider visible={matchDeletionMode || isMissingReceiptsMode}>
              {(matchDeletionMode || isMissingReceiptsMode) && (
                <SelectionPanelActions
                  isDeleteMode={matchDeletionMode}
                  onSelectionChange={setSelectedIds}
                  onDelete={matchDeletionMode ? requestDeleteSelectedLines : undefined}
                  onEditFinish={handleEditFinish}
                  selectedIds={selectedIds}
                />
              )}
            </Styled.NavSlider>
            <Flex
              alignItems="center"
              justifyContent="flex-start"
              gap={Spacing.S8}
              css={css`
                font-size: 14px;
              `}
            >
              {t('bankreconciliation.matchgroups.totalgroups', { count: bankLineMatchCount ?? 10 })}
              <Flex alignItems="center" flexDirection="row">
                <ButtonsGroup>
                  <ToggleDeletionModeButton onClick={() => setMatchDeletionMode(true)} />
                  <DownloadLinesButton />
                  <ToggleMissingReceiptsModeButton onClick={enableMissingReceiptsMode} />
                  <BulkReconciliationButton />
                  <ReconcileAllButton />
                </ButtonsGroup>
              </Flex>
            </Flex>
            <SearchBoxIcon
              id="bankLineGroupSearch"
              onValueChange={onChangeBankLineGroupSearchValue}
              placeholder={t('search_box.default_placeholder')}
            />
            <FilterDropdownButton dropdownOrigin="right">
              <BankLineGroupFiltersPanel />
            </FilterDropdownButton>
          </Flex>
        </div>
        <div>
          <Styled.BankLogoWrapper>
            <Logo />
          </Styled.BankLogoWrapper>
          <Flex
            justifyContent="flex-end"
            className={reactClass('list-filters')}
            gap={Spacing.S8}
            css={css`
              padding-bottom: 10px;
            `}
          >
            <SearchBoxIcon
              id="reconcilablePostingSearch"
              onValueChange={onChangeReconcilablePostingSearchValue}
              placeholder={t('search_box.default_placeholder')}
            />
            <FilterDropdownButton dropdownOrigin="right">
              <ReconcilablePostingFiltersPanel />
            </FilterDropdownButton>
          </Flex>
        </div>
        <MatchList
          bulkSelectionMode={matchDeletionMode || isMissingReceiptsMode}
          isDeleteMode={matchDeletionMode}
          onChangeSelectedIds={setSelectedIds}
          selectedIds={selectedIds}
        />
        <TransactionList />
      </div>
    </DragDropContext>
  )
}
